import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FILE_URL } from "@/lib/api";
import { X, Link as LinkIcon, Check, Mail, Send, Share2, Instagram } from "lucide-react";
import { toast } from "sonner";
import { buildInstagramStoryCard } from "@/lib/instagramStoryCard";

/**
 * A soft, ad-free share sheet. Prefers the native Web Share API when available
 * (mobile), and always offers a copy-link plus warm fallbacks (WhatsApp, Email, X).
 */
export default function ShareSheet({ idea, open, onClose }) {
  const [copied, setCopied] = useState(false);
  const [igBusy, setIgBusy] = useState(false);
  // Share URL uses REACT_APP_PUBLIC_URL when set (willoah.com in production)
  // so branch links Kelly sends always land on the branded domain regardless
  // of which environment the sender opened.
  const publicOrigin = process.env.REACT_APP_PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const url = `${publicOrigin}/idea/${idea?.id}`;
  const text = idea ? `${idea.title} — a real idea from ${idea.author_name} on Willoah.` : "";

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !idea) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast("Link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast("Could not copy — try selecting the link manually.");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: idea.title, text, url });
      onClose?.();
    } catch {
      // user dismissed — ignore
    }
  };

  /**
   * Build the branded 1080×1920 story card on-canvas, then either:
   *   (a) hand the PNG file off to the OS share sheet via
   *       `navigator.share({files:[...]})` — most modern mobile browsers
   *       expose Instagram → Story as a target from that sheet; or
   *   (b) download the PNG so the user can open Instagram and add it as
   *       a story manually (desktop or older browsers).
   *
   * Instagram has no direct web-to-story API; this "hand off a ready-made
   * image" pattern is the practical alternative and works cleanly on iOS
   * (Share sheet → Instagram → "Stories") and modern Android.
   */
  const shareToInstagramStory = async () => {
    if (!idea) return;
    setIgBusy(true);
    try {
      const blob = await buildInstagramStoryCard(idea);
      const filename = `willoah-${(idea.title || "seed").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "seed"}.png`;
      const file = new File([blob], filename, { type: "image/png" });

      const canShareFile =
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] });

      if (canShareFile) {
        try {
          await navigator.share({
            files: [file],
            title: idea.title,
            text: `${text}\n${url}`,
          });
          toast("Story card ready — pick Instagram → Stories.");
          onClose?.();
          return;
        } catch {
          // User cancelled or share threw — fall through to download.
        }
      }

      // Fallback: download the PNG. Show a small toast with instructions.
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast("Story card downloaded. Open Instagram → tap + → Story → pick this image.", { duration: 6000 });
    } catch (err) {
      toast("Could not build the story card. Try again in a moment.");
    } finally {
      setIgBusy(false);
    }
  };

  const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  const encoded = encodeURIComponent(`${text} ${url}`);
  const linkBtn = "inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm border border-[#B79256]/40 bg-white text-[#2A2823] hover:bg-[#B79256]/12 transition-colors";

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-[#2A2823]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="share-sheet"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FBF8F1] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-[#B79256]/30 shadow-[0_30px_80px_rgb(42,40,35,0.2)] overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#B79256]/25">
          <div>
            <div className="accent-label text-[#B79256]">share this branch</div>
            <div className="text-xs text-[#2A2823]/50 mt-1">Real people. No algorithm.</div>
          </div>
          <button
            onClick={onClose}
            data-testid="share-close"
            aria-label="Close share sheet"
            className="p-2 rounded-full hover:bg-[#B79256]/15 text-[#2A2823]/60 hover:text-[#2A2823] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Warm preview mini-card */}
        <div className="p-5 flex gap-4 border-b border-[#B79256]/20">
          <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#B79256]/10 border border-[#B79256]/20 flex-shrink-0">
            {idea.photo_path && (
              <img src={FILE_URL(idea.photo_path)} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="min-w-0">
            <div className="accent-label text-[#B79256]">{idea.category}</div>
            <div className="font-serif-display text-xl text-[#2A2823] leading-tight mt-0.5">{idea.title}</div>
            <div className="text-xs text-[#2A2823]/60 mt-1">by {idea.author_name}</div>
          </div>
        </div>

        {/* Link row */}
        <div className="px-5 pt-5">
          <div className="accent-label text-[#B79256]">the link</div>
          <div className="mt-2 flex items-center gap-2">
            <div data-testid="share-url" className="flex-1 truncate rounded-full bg-white border border-[#B79256]/30 px-4 h-11 flex items-center text-sm text-[#2A2823]/85">
              {url}
            </div>
            <button
              data-testid="share-copy"
              onClick={copyLink}
              className={`inline-flex items-center gap-2 px-4 h-11 rounded-full text-sm shadow-none transition-colors ${
                copied
                  ? "bg-[#4E5A3D] text-[#FBF8F1] border border-[#4E5A3D]"
                  : "bg-[#4E5A3D] text-[#FBF8F1] border border-[#4E5A3D] hover:bg-[#3E4830]"
              }`}
            >
              {copied ? <><Check className="w-4 h-4" strokeWidth={2}/>Copied</> : <><LinkIcon className="w-4 h-4" strokeWidth={1.75}/>Copy link</>}
            </button>
          </div>
        </div>

        {/* Fallbacks */}
        <div className="px-5 pt-5 pb-6">
          <div className="accent-label text-[#B79256]">or share via</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {hasNativeShare && (
              <button
                type="button"
                data-testid="share-native"
                onClick={nativeShare}
                className={linkBtn}
              >
                <Share2 className="w-4 h-4" strokeWidth={1.75}/> Your device
              </button>
            )}
            <button
              type="button"
              data-testid="share-instagram-story"
              onClick={shareToInstagramStory}
              disabled={igBusy}
              className={`${linkBtn} disabled:opacity-50`}
              title="Get a branded story card ready for Instagram"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.75}/>
              {igBusy ? "Preparing…" : "Instagram Story"}
            </button>
            <a
              data-testid="share-whatsapp"
              href={`https://wa.me/?text=${encoded}`}
              target="_blank"
              rel="noopener noreferrer"
              className={linkBtn}
            >
              <Send className="w-4 h-4" strokeWidth={1.75}/> WhatsApp
            </a>
            <a
              data-testid="share-email"
              href={`mailto:?subject=${encodeURIComponent(idea.title)}&body=${encoded}`}
              className={linkBtn}
            >
              <Mail className="w-4 h-4" strokeWidth={1.75}/> Email
            </a>
          </div>
          <p className="mt-5 text-xs text-[#2A2823]/50 leading-relaxed">
            Willoah doesn't attach tracking codes to shared links. Your friend gets a plain branch page — and any sponsored content is always clearly labeled.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
